import React, { useState, useEffect } from "react";
import { Spinner } from "@amzn/stencil-react-components/spinner";
import { buildUrl, TASKSTATUS_ENDPOINT } from "../config/urls";
import { getCandidateData } from "../utility/candidate-data";
import { fetchWithTimeout } from "../helpers/fetcher";

/**
 * Current statuses for default tasks
 */
export enum StatusEnum {
  HIDDEN = "Hidden",
  LOADING = "Loading",
  NOT_STARTED = "Not Started",
  IN_PROGRESS = "In Progress",
  COMPLETED = "Completed",
  API_ERROR = "N/A",
  ERROR = "Error",
  PENDING_REVIEW = "Pending Review By Amazon",
  ELIGIBLE = "Eligible",
  INELIGIBLE = "Ineligible",
}

const TaskStatusMap: Record<string, StatusEnum> = {
  ELIGIBLE: StatusEnum.ELIGIBLE,
  IN_PROGRESS: StatusEnum.IN_PROGRESS,
  SUBMITTED: StatusEnum.COMPLETED,
  COMPLETED: StatusEnum.COMPLETED,
  INELIGIBLE: StatusEnum.INELIGIBLE,
};

interface WithStatusProps {
  key: string;
  renderComponent: (status?: StatusEnum) => JSX.Element;
  /**
   * Templated path i.e. 'api/resource1/123/resource2/456'
   */
  statusPath?: string;
  applicationId?: string;
  candidateId?: string;
  hideWhenError?: boolean;
  isFallback?: boolean;
  shouldOverrideStatus?: Record<string, StatusEnum>;
  taskStatusName?: string;
  hideOnTaskStatusError?: boolean;
}

const getStatus = async ({ applicationId, statusPath }: any) => {
  try {
    const { candidateId, sfCandidateId } = await getCandidateData(
      applicationId
    );

    const url = buildUrl(statusPath, {
      candidateId,
      sfCandidateId,
      applicationId,
    });
    const { status } = await fetchWithTimeout(url);
    return status;
  } catch (e) {
    console.error(
      `Failed to retrieve status with path: ${statusPath} . Message: ${e.message}`
    );
    return StatusEnum.API_ERROR;
  }
};

const getTaskStatus = async ({ applicationId, taskStatusName }: any) => {
  try {
    const { candidateId, sfCandidateId } = await getCandidateData(
      applicationId
    );

    const url = buildUrl(TASKSTATUS_ENDPOINT, {
      candidateId,
      sfCandidateId,
      applicationId,
    });
    const taskStatus = await fetchWithTimeout(url);

    if (taskStatus.errorCode) {
      if (taskStatus.errorCode != 404) {
        throw Error(
          `Error when requesting taskstatus from ${url}: ${taskStatus.errorCode}, ${taskStatus.errorMessage}`
        );
      }
      return StatusEnum.API_ERROR;
    }
    const taskStatusObject = taskStatus?.find(
      (obj: { taskType: string }) => obj.taskType === taskStatusName
    );
    if (!taskStatusObject.taskStatus) {
      return StatusEnum.API_ERROR;
    }
    return TaskStatusMap[taskStatusObject.taskStatus];
  } catch (e) {
    console.error(
      `Failed to retrieve status for task: ${taskStatusName} . Message: ${e.message}`
    );
    return StatusEnum.API_ERROR;
  }
};

export const withStatus = ({
  key,
  statusPath,
  applicationId,
  hideWhenError,
  isFallback,
  renderComponent,
  shouldOverrideStatus,
  taskStatusName,
  hideOnTaskStatusError,
}: WithStatusProps) => {
  const [status, setStatus] = useState<StatusEnum>(StatusEnum.LOADING);

  useEffect(() => {
    if (!statusPath && !taskStatusName) {
      return;
    }
    statusPath &&
      getStatus({ applicationId, statusPath }).then(function (status) {
        if (shouldOverrideStatus && shouldOverrideStatus[status]) {
          setStatus(shouldOverrideStatus[status]);
        } else {
          setStatus(status);
        }
      });
    taskStatusName &&
      getTaskStatus({ applicationId, taskStatusName }).then(function (status) {
        if (shouldOverrideStatus && shouldOverrideStatus[status]) {
          setStatus(shouldOverrideStatus[status]);
        } else {
          setStatus(status);
        }
      });
  }, [statusPath]);

  if (!statusPath && !taskStatusName) {
    return renderComponent(undefined);
  }

  if (status === StatusEnum.LOADING) {
    return <Spinner key={key} loadingText="Loading task..." showText />;
  }

  // Hide task if the status is Hidden
  if (!isFallback && status === StatusEnum.HIDDEN) {
    return null;
  }

  // Hide fallback if the status is not Hidden and not Error
  if (
    isFallback &&
    status !== StatusEnum.HIDDEN &&
    status !== StatusEnum.API_ERROR
  ) {
    return null;
  }

  if (hideWhenError && status === StatusEnum.API_ERROR) {
    if (process.env.NODE_ENV === "production" || hideOnTaskStatusError) {
      return null;
    }
    console.warn(
      `The task "${key}" has "hideWhenError" set to true, that means it will be hidden in prod`
    );
  }

  return renderComponent(status);
};
